import { CheckCircle2, AlertTriangle, XCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import type { RouteType } from "@/lib/genrl";
import { CreditAmount } from "./CreditAmount";

interface Props {
  routeType: RouteType;
  availableCreditMicros: string | number;
  requiredReserveMicros: string | number;
  providerBalanceMicros?: string | number;
  providerThresholdMicros?: string | number;
  className?: string;
}

export type ReadinessState =
  | "READY_INTERNAL"
  | "READY_PEER"
  | "INSUFFICIENT_CREDITS"
  | "PROVIDER_LOW_BALANCE"
  | "BLOCKED";

export function computeReadiness(p: Omit<Props, "className">): ReadinessState {
  const avail = Number(p.availableCreditMicros);
  const reserve = Number(p.requiredReserveMicros);
  const bal = p.providerBalanceMicros !== undefined ? Number(p.providerBalanceMicros) : undefined;
  const thr = p.providerThresholdMicros !== undefined ? Number(p.providerThresholdMicros) : undefined;
  if (p.routeType === "INTERNAL") {
    if (bal !== undefined && thr !== undefined && bal < thr) return "PROVIDER_LOW_BALANCE";
    return "READY_INTERNAL";
  }
  if (avail < reserve) return "INSUFFICIENT_CREDITS";
  return "READY_PEER";
}

const COPY: Record<ReadinessState, { label: string; tone: "ok" | "warn" | "block"; Icon: typeof CheckCircle2 }> = {
  READY_INTERNAL:        { label: "Ready to run on internal provider", tone: "ok",    Icon: CheckCircle2 },
  READY_PEER:            { label: "Ready to run",                       tone: "ok",    Icon: CheckCircle2 },
  INSUFFICIENT_CREDITS:  { label: "Add credits to run",                 tone: "block", Icon: XCircle },
  PROVIDER_LOW_BALANCE:  { label: "Provider balance below threshold",   tone: "warn",  Icon: AlertTriangle },
  BLOCKED:               { label: "Blocked",                             tone: "block", Icon: XCircle },
};

export function CreditReadiness(p: Props) {
  const state = computeReadiness(p);
  const { label, tone, Icon } = COPY[state];
  const cls =
    tone === "ok"
      ? "border-[var(--success)]/30 bg-[var(--success)]/10 text-[var(--success)]"
      : tone === "warn"
        ? "border-[var(--warning)]/30 bg-[var(--warning)]/10 text-[var(--warning)]"
        : "border-destructive/30 bg-destructive/10 text-destructive";

  return (
    <div className={cn("rounded-xl border p-3", cls, p.className)}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4" />
        <p className="text-sm font-medium">{label}</p>
      </div>
      <div className="mt-2 grid gap-1 text-[11px] text-muted-foreground">
        {p.routeType !== "INTERNAL" && (
          <>
            <Row k="Available" v={<CreditAmount micros={p.availableCreditMicros} />} />
            <Row k="Required reserve" v={<CreditAmount micros={p.requiredReserveMicros} />} />
          </>
        )}
        {p.routeType === "INTERNAL" && p.providerBalanceMicros !== undefined && (
          <Row k="Provider balance" v={<CreditAmount micros={p.providerBalanceMicros} suffix="USD" />} />
        )}
      </div>
    </div>
  );
}

function Row({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between">
      <span>{k}</span>
      <span className="text-foreground">{v}</span>
    </div>
  );
}
